import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./SocialLinksPage.module.scss";
import Header from "../Header/header";
import SetupSteps, { Step } from "../Register/SetupSteps/SetupSteps";
import axios from "axios";
import {
  FaFacebookF,
  FaInstagram,
  FaWhatsapp,
  FaLinkedin,
  FaGlobe,
  FaEnvelope,
  FaGoogle,
} from "react-icons/fa";
import { SiTiktok, SiGooglemaps } from "react-icons/si";

const STEPS: Step[] = [
  { key: "practice", label: "Practice and personal info" },
  { key: "logo", label: "Logo Management" },
  { key: "social", label: "Social Media Links" },
  { key: "portfolio", label: "Portfolio Gallery" },
  { key: "consent", label: "Consent Templates" },
  { key: "preview", label: "Preview & Complete" },
];

type SocialLinks = {
  google_review?: string;
  instagram?: string;
  tiktok?: string;
  facebook?: string;
  whatsapp?: string;
  linkedin?: string;
  google_maps?: string;
  website?: string;
  email?: string;
};

// ✅ Field order
const FIELDS: (keyof SocialLinks)[] = [
  "google_review",
  "instagram",
  "tiktok",
  "facebook",
  "whatsapp",
  "linkedin",
  "google_maps",
  "website",
  "email",
];

const ICONS: Record<string, JSX.Element> = {
  facebook: <FaFacebookF color="#1877F2" size={22} />,
  instagram: <FaInstagram color="#E4405F" size={22} />,
  whatsapp: <FaWhatsapp color="#25D366" size={22} />,
  tiktok: <SiTiktok color="#000" size={22} />,
  linkedin: <FaLinkedin color="#0077B5" size={22} />,
  google_review: (
    <div className={styles.googleReviewIcon}>
      <FaGoogle color="#EA4335" size={22} />
      <span className={styles.googleReviewText}>Review</span>
    </div>
  ),
  google_maps: <SiGooglemaps color="#34A853" size={22} />,
  website: <FaGlobe color="#1f6fff" size={22} />,
  email: <FaEnvelope color="#EA4335" size={22} />,
};

export default function SocialLinksPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState<"success" | "error" | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [socialLinks, setSocialLinks] = useState<SocialLinks>(
    FIELDS.reduce((acc, field) => ({ ...acc, [field]: "" }), {})
  );

  // ✅ Fetch user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data } = await axios.get("/user");
        if (Array.isArray(data) && data.length > 1) {
          setUser(data[1]);
        }
      } catch (err) {
        console.error("Error fetching user:", err);
      }
    };
    fetchUser();
  }, []);

  // ✅ Validation logic
  const validateField = (field: keyof SocialLinks, value: string) => {

  if (field === "google_review") {
    setErrors((prev) => ({ ...prev, [field]: "" }));
    return;
  }
  let error = "";
  if (!value.trim()) {
    setErrors((prev) => ({ ...prev, [field]: "" }));
    return;
  }

  const urlPatterns: Record<string, RegExp> = {
    facebook: /^https?:\/\/(www\.)?facebook\.com\/[A-Za-z0-9._-]+/,
    instagram: /^https?:\/\/(www\.)?instagram\.com\/[A-Za-z0-9._-]+/,
    tiktok: /^https?:\/\/(www\.)?tiktok\.com\/(@)?[A-Za-z0-9._-]+/,
    whatsapp: /^https?:\/\/(wa\.me|api\.whatsapp\.com)\/[0-9]+/,
    linkedin: /^https?:\/\/(www\.)?linkedin\.com\/(in|company)\/[A-Za-z0-9._-]+/,
    google_maps: /^https?:\/\/(goo\.gl\/maps|www\.google\.com\/maps)/,
    website: /^https?:\/\/[A-Za-z0-9.-]+\.[A-Za-z]{2,}/,
  };

  if (field === "email") {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) error = "Invalid email address";
  } else if (urlPatterns[field]) {
    if (!urlPatterns[field].test(value)) error = `Please enter a valid ${field} link`;
  } else if (!/^https?:\/\//i.test(value)) {
    error = "URL must start with http or https";
  }

  setErrors((prev) => ({ ...prev, [field]: error }));
};

  const handleChange = (field: keyof SocialLinks, value: string) => {
    setSocialLinks((prev) => ({ ...prev, [field]: value }));
    validateField(field, value);
  };

  const allValid = Object.values(errors).every((e) => !e);

  // ✅ Save links
 const saveLinks = async () => {
  setLoading(true);
  try {
    let hasError = false;
    Object.entries(socialLinks).forEach(([key, val]) => {
      validateField(key as keyof SocialLinks, val || "");
      if (val && errors[key]) hasError = true;
    });
    if (hasError) {
      setLoading(false);
      return;
    }

    // ✅ Choose endpoint based on user level
    const endpoint =
      user?.level === "user"
        ? `${process.env.REACT_APP_BASE_URL_V2}onboarding/update_practitioner_data`
        : `${process.env.REACT_APP_BASE_URL_V2}onboarding/update_practice_data`;

    console.log("Saving to endpoint:", endpoint);

    await axios.put(
      endpoint,
      { social_links: socialLinks },
      { headers: { Accept: "application/json", "Content-Type": "application/json" } }
    );

    setShowModal("success");
    setTimeout(() => {
      setShowModal(null);
      navigate("/portfolioGallery");
    }, 2000);
  } catch (err) {
    console.error("Error saving links:", err);
    setShowModal("error");
    setTimeout(() => setShowModal(null), 2000);
  } finally {
    setLoading(false);
  }
};

  return (
    <div className={styles.page}>
      <Header />
      <main className={styles.main}>
        <SetupSteps steps={STEPS} current={3} rightLabel="20% Complete" />

        <h1 className={styles.title}>Social media links</h1>
        <p className={styles.subtitle}>
          Add your practice’s social media links so patients can connect with you online.
        </p>

        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Social media</h2>

          {FIELDS.map((field) => (
            <div key={field} className={styles.linkBox}>
              <div className={styles.iconBox}>{ICONS[field]}</div>
              <div className={styles.inputWrap}>
                <input
                  className={`${styles.input} ${errors[field] ? styles.errorInput : ""}`}
                  placeholder={`Enter ${field.replace("_", " ")} link`}
                  value={socialLinks[field] || ""}
                  onChange={(e) => handleChange(field, e.target.value)}
                />
                {errors[field] && <small className={styles.errorText}>{errors[field]}</small>}
              </div>
            </div>
          ))}
        </section>

        <div className={styles.actions}>
          <button
            className={styles.btnSecondary}
            onClick={() => navigate("/logoManagement")}
          >
            Back
          </button>
          <button
            className={styles.btnPrimary}
            disabled={loading || !allValid}
            onClick={saveLinks}
          >
            {loading ? "Saving..." : "Save & Continue"}
          </button>
        </div>
      </main>

      <footer className={styles.footerBar}>
        Copyright © Medicube 2025. All Rights Reserved.
      </footer>

      {showModal && (
        <div className={styles.modalOverlay}>
          <div
            className={`${styles.modalBox} ${
              showModal === "success" ? styles.success : styles.error
            }`}
          >
            {showModal === "success"
              ? "Saved successfully!"
              : "Something went wrong!"}
          </div>
        </div>
      )}
    </div>
  );
}
